import type { Locale } from '@360face/domain';

/**
 * «Learn more» text (owner 24.10.2026): text only, no pictures — what the treatment includes and
 * which muscles we focus on. EN + PT; other kiosk languages fall back to EN.
 * Matched by the treatment's name, like the 3D pictures (art.ts).
 * Muscle sets: the owner's list of 24.10.2026 (to be reviewed with the therapists).
 */
export type MuscleId =
  | 'scalp'
  | 'frontalis'
  | 'temporalis'
  | 'orbicularis_oculi'
  | 'zygomaticus'
  | 'levator_labii'
  | 'masseter'
  | 'buccinator'
  | 'orbicularis_oris'
  | 'mentalis'
  | 'platysma'
  | 'scm'
  | 'trapezius'
  | 'pectoralis'
  | 'splenius'
  | 'levator_scapulae'
  | 'rhomboids';

export const MUSCLES: Record<MuscleId, { en: string; pt: string }> = {
  scalp: { en: 'Scalp', pt: 'Couro cabeludo' },
  frontalis: { en: 'Frontalis · forehead', pt: 'Frontal · testa' },
  temporalis: { en: 'Temporalis', pt: 'Temporal' },
  orbicularis_oculi: { en: 'Orbicularis oculi · eyes', pt: 'Orbicular dos olhos' },
  zygomaticus: { en: 'Zygomaticus major & minor', pt: 'Zigomáticos maior e menor' },
  levator_labii: { en: 'Levator labii superioris', pt: 'Elevador do lábio superior' },
  masseter: { en: 'Masseter · jaw', pt: 'Masséter · maxilar' },
  buccinator: { en: 'Buccinator · cheeks', pt: 'Bucinador · bochechas' },
  orbicularis_oris: { en: 'Orbicularis oris · mouth', pt: 'Orbicular da boca' },
  mentalis: { en: 'Mentalis · chin', pt: 'Mentoniano · queixo' },
  platysma: { en: 'Platysma · neck', pt: 'Platisma · pescoço' },
  scm: { en: 'Sternocleidomastoid', pt: 'Esternocleidomastóideo' },
  trapezius: { en: 'Trapezius · shoulders', pt: 'Trapézio · ombros' },
  pectoralis: { en: 'Pectoralis · décolleté', pt: 'Peitorais · decote' },
  splenius: { en: 'Splenius · back of the neck', pt: 'Esplénio · nuca' },
  levator_scapulae: { en: 'Levator scapulae', pt: 'Elevador da escápula' },
  rhomboids: { en: 'Rhomboids · upper back', pt: 'Romboides · parte superior das costas' },
};

/** 1 = main muscles, 0.5 = also worked (lighter). */
export type MuscleSet = Partial<Record<MuscleId, 1 | 0.5>>;

export interface MuscleCard {
  /** What the treatment includes. */
  lead: Partial<Record<Locale, string>> & { en: string };
  /** Muscles we focus on; absent for body massage (the set depends on the request). */
  muscles?: MuscleSet;
  /** Which tiers include a muscle (Face Workout). */
  notes?: Partial<Record<MuscleId, Partial<Record<Locale, string>> & { en: string }>>;
  /** Body drainage: lymph flow instead of a muscle list. */
  body?: 'lymph';
}

const FACE: MuscleSet = {
  frontalis: 1,
  orbicularis_oculi: 1,
  zygomaticus: 1,
  masseter: 1,
  buccinator: 1,
  orbicularis_oris: 1,
  mentalis: 1,
  platysma: 1,
  scm: 1,
};

/** One text for the four tiers: the kiosk shows them as one card with four lengths. */
const FACE_WORKOUT: MuscleCard = {
  lead: {
    en: 'Starts with the shoulders, chest and neck, then an intense facial lifting massage, drainage and gua sha. Shoulders — from Optimal. Head massage — in Maximum, optional in Maximum Plus.',
    pt: 'Começa pelos ombros, peito e pescoço; depois lifting facial intenso, drenagem e gua sha. Ombros — a partir do Optimal. Massagem da cabeça — no Maximum, opcional no Maximum Plus.',
    fr: 'Commence par les épaules, la poitrine et le cou, puis un massage liftant intense, drainage et gua sha. Épaules — à partir d’Optimal. Massage de la tête — dans Maximum, en option dans Maximum Plus.',
    es: 'Empieza por hombros, pecho y cuello; luego lifting facial intenso, drenaje y gua sha. Hombros — desde Optimal. Masaje de cabeza — en Maximum, opcional en Maximum Plus.',
    ru: 'Начинаем с плеч, груди и шеи, затем интенсивный лифтинг-массаж лица, дренаж и гуаша. Плечи — с Optimal. Массаж головы — в Maximum, по желанию в Maximum Plus.',
    uk: 'Починаємо з плечей, грудей і шиї, потім інтенсивний ліфтинг-масаж обличчя, дренаж і гуаша. Плечі — з Optimal. Масаж голови — у Maximum, за бажанням у Maximum Plus.',
    de: 'Beginnt mit Schultern, Brust und Nacken, dann intensive Lifting-Gesichtsmassage, Drainage und Gua Sha. Schultern — ab Optimal. Kopfmassage — in Maximum, optional in Maximum Plus.',
  },
  muscles: { ...FACE, trapezius: 0.5, pectoralis: 0.5, scalp: 0.5 },
  notes: {
    trapezius: {
      en: 'from Optimal',
      pt: 'a partir do Optimal',
      fr: 'à partir d’Optimal',
      es: 'desde Optimal',
      ru: 'с Optimal',
      uk: 'з Optimal',
      de: 'ab Optimal',
    },
    pectoralis: {
      en: 'from Optimal',
      pt: 'a partir do Optimal',
      fr: 'à partir d’Optimal',
      es: 'desde Optimal',
      ru: 'с Optimal',
      uk: 'з Optimal',
      de: 'ab Optimal',
    },
    scalp: { en: 'Maximum, Maximum Plus', pt: 'Maximum, Maximum Plus' },
  },
};

const CARDS: Array<[RegExp, MuscleCard]> = [
  [
    /kobido/i,
    {
      lead: {
        en: 'Japanese technique: fast rhythmic movements, pressure, kneading and lifting on the face, neck and décolleté. Ends with lymphatic drainage to the neck nodes.',
        pt: 'Técnica japonesa: movimentos rápidos e rítmicos, pressão, amassamento e lifting no rosto, pescoço e decote. Termina com drenagem linfática para os gânglios do pescoço.',
        fr: 'Technique japonaise : mouvements rapides et rythmés, pression, pétrissage et lifting du visage, du cou et du décolleté. Se termine par un drainage lymphatique vers les ganglions du cou.',
        es: 'Técnica japonesa: movimientos rápidos y rítmicos, presión, amasamiento y lifting en rostro, cuello y escote. Termina con drenaje linfático hacia los ganglios del cuello.',
        ru: 'Японская техника: быстрые ритмичные движения, давление, разминание и подтягивание лица, шеи и декольте. В конце — лимфодренаж к узлам шеи.',
        uk: 'Японська техніка: швидкі ритмічні рухи, тиск, розминання та підтягування обличчя, шиї й декольте. Наприкінці — лімфодренаж до вузлів шиї.',
        de: 'Japanische Technik: schnelle, rhythmische Bewegungen, Druck, Kneten und Lifting an Gesicht, Hals und Dekolleté. Zum Abschluss Lymphdrainage zu den Halslymphknoten.',
      },
      muscles: { ...FACE, pectoralis: 0.5 },
    },
  ],
  [
    /korugi/i,
    {
      lead: {
        en: 'Korean technique: strong, deep pressure in the facial muscles. Lifts the cheekbones and reshapes the face oval and chin.',
        pt: 'Técnica coreana: pressão forte e profunda nos músculos do rosto. Eleva as maçãs do rosto e redefine o oval e o queixo.',
        fr: 'Technique coréenne : pression forte et profonde dans les muscles du visage. Remonte les pommettes et redessine l’ovale et le menton.',
        es: 'Técnica coreana: presión fuerte y profunda en los músculos del rostro. Eleva los pómulos y redefine el óvalo y la barbilla.',
        ru: 'Корейская техника: сильное глубокое давление на мышцы лица. Поднимает скулы, перестраивает овал и подбородок.',
        uk: 'Корейська техніка: сильний глибокий тиск на м’язи обличчя. Піднімає вилиці, змінює овал і підборіддя.',
        de: 'Koreanische Technik: kräftiger, tiefer Druck in die Gesichtsmuskeln. Hebt die Wangenknochen und formt Gesichtsoval und Kinn neu.',
      },
      muscles: { zygomaticus: 1, masseter: 1, temporalis: 1, frontalis: 1, levator_labii: 1, mentalis: 0.5 },
    },
  ],
  [
    /buccal|bucal/i,
    {
      lead: {
        en: 'Releases tension and puffiness in the cheeks; worked from outside and from inside the mouth, in gloves. Defines the lower face and jaw line.',
        pt: 'Alivia a tensão e o inchaço das bochechas; trabalho por fora e por dentro da boca, com luvas. Define a parte inferior do rosto e a linha do maxilar.',
        fr: 'Relâche les tensions et le gonflement des joues ; travail à l’extérieur et à l’intérieur de la bouche, avec des gants. Dessine le bas du visage et la mâchoire.',
        es: 'Alivia la tensión y la hinchazón de las mejillas; trabajo por fuera y por dentro de la boca, con guantes. Define la parte inferior del rostro y la mandíbula.',
        ru: 'Снимает напряжение и отёчность щёк; работа снаружи и изнутри рта, в перчатках. Очерчивает нижнюю часть лица и линию челюсти.',
        uk: 'Знімає напругу та набряклість щік; робота ззовні та зсередини рота, в рукавичках. Окреслює нижню частину обличчя й лінію щелепи.',
        de: 'Löst Spannung und Schwellungen der Wangen; Arbeit von außen und von innen im Mund, mit Handschuhen. Konturiert das untere Gesicht und die Kieferlinie.',
      },
      muscles: { buccinator: 1, masseter: 1, orbicularis_oris: 1, zygomaticus: 0.5, mentalis: 0.5 },
    },
  ],
  [
    /face ?\+ ?upper|rosto ?\+ ?(parte )?superior/i,
    {
      lead: {
        en: 'Facial massage plus the neck, shoulders and upper back — the muscles that pull the face down.',
        pt: 'Massagem facial mais pescoço, ombros e parte superior das costas — os músculos que puxam o rosto para baixo.',
        fr: 'Massage du visage plus le cou, les épaules et le haut du dos — les muscles qui tirent le visage vers le bas.',
        es: 'Masaje facial más cuello, hombros y parte alta de la espalda — los músculos que tiran del rostro hacia abajo.',
        ru: 'Массаж лица плюс шея, плечи и верх спины — мышцы, которые тянут лицо вниз.',
        uk: 'Масаж обличчя плюс шия, плечі та верх спини — м’язи, які тягнуть обличчя вниз.',
        de: 'Gesichtsmassage plus Nacken, Schultern und oberer Rücken — die Muskeln, die das Gesicht nach unten ziehen.',
      },
      muscles: {
        platysma: 1,
        scm: 1,
        trapezius: 1,
        rhomboids: 1,
        levator_scapulae: 1,
        frontalis: 0.5,
        orbicularis_oculi: 0.5,
        zygomaticus: 0.5,
        masseter: 0.5,
        buccinator: 0.5,
        orbicularis_oris: 0.5,
        mentalis: 0.5,
      },
    },
  ],
  [
    /head ?(&|and|e) ?shoulders|cabe[cç]a e ombros/i,
    {
      lead: {
        en: 'Releases neck and shoulder tension from desk work, posture and stress. Includes the scalp.',
        pt: 'Alivia a tensão do pescoço e ombros causada pelo trabalho sentado, postura e stress. Inclui o couro cabeludo.',
        fr: 'Relâche les tensions du cou et des épaules dues au travail assis, à la posture et au stress. Cuir chevelu inclus.',
        es: 'Alivia la tensión de cuello y hombros por el trabajo sentado, la postura y el estrés. Incluye el cuero cabelludo.',
        ru: 'Снимает напряжение шеи и плеч от сидячей работы, осанки и стресса. Включает массаж головы.',
        uk: 'Знімає напругу шиї та плечей від сидячої роботи, постави й стресу. Включає масаж голови.',
        de: 'Löst Nacken- und Schulterverspannungen durch Sitzen, Haltung und Stress. Inklusive Kopfhaut.',
      },
      muscles: { trapezius: 1, splenius: 1, scm: 1, levator_scapulae: 1, scalp: 1 },
    },
  ],
  [
    /lymph|linf|slim|cellul|celul/i,
    {
      lead: {
        en: 'Drainage and sculpting with hands and tools: abdomen, then legs and hips. The focus is lymph flow, not muscles.',
        pt: 'Drenagem e modelação com as mãos e instrumentos: abdómen, depois pernas e ancas. O foco é o fluxo linfático, não os músculos.',
        fr: 'Drainage et modelage avec les mains et des outils : ventre, puis jambes et hanches. L’objectif est le flux lymphatique, pas les muscles.',
        es: 'Drenaje y modelado con las manos e instrumentos: abdomen, luego piernas y caderas. El foco es el flujo linfático, no los músculos.',
        ru: 'Дренаж и моделирование руками и инструментами: живот, затем ноги и бёдра. Главное — лимфоток, а не мышцы.',
        uk: 'Дренаж і моделювання руками та інструментами: живіт, потім ноги й стегна. Головне — лімфотік, а не м’язи.',
        de: 'Drainage und Modellierung mit Händen und Instrumenten: Bauch, dann Beine und Hüften. Im Fokus steht der Lymphfluss, nicht die Muskeln.',
      },
      body: 'lymph',
    },
  ],
  [
    /prenatal|pr[eé].?natal/i,
    {
      lead: {
        en: 'A gentle body massage adapted to your trimester.',
        pt: 'Massagem corporal suave, adaptada ao seu trimestre.',
        fr: 'Un massage du corps tout en douceur, adapté à votre trimestre.',
        es: 'Masaje corporal suave, adaptado a tu trimestre.',
        ru: 'Мягкий массаж тела с учётом вашего триместра.',
        uk: 'М’який масаж тіла з урахуванням вашого триместру.',
        de: 'Sanfte Körpermassage, angepasst an Ihr Trimester.',
      },
    },
  ],
  [
    /body massage|massagem corporal/i,
    {
      lead: {
        en: 'Deep, lymphatic or tailored to your request — the therapist adapts the pressure and the focus areas.',
        pt: 'Profunda, linfática ou à medida — a terapeuta adapta a pressão e as zonas de foco.',
        fr: 'Profond, lymphatique ou sur mesure — la thérapeute adapte la pression et les zones ciblées.',
        es: 'Profundo, linfático o a medida — la terapeuta adapta la presión y las zonas de interés.',
        ru: 'Глубокий, лимфатический или по запросу — мастер подбирает давление и зоны.',
        uk: 'Глибокий, лімфатичний або на запит — майстер підбирає тиск і зони.',
        de: 'Tief, lymphatisch oder individuell — die Therapeutin passt Druck und Zonen an.',
      },
    },
  ],
  [/maximum|optimal|express(?!\s+face\s+peel)|workout|training|facial lifting|first .*facial/i, FACE_WORKOUT],
];

export function muscleCard(title: { en?: string; pt?: string }): MuscleCard | undefined {
  const text = [title.en, title.pt].filter(Boolean).join(' ');
  return CARDS.find(([re]) => re.test(text))?.[1];
}
